import React, {useEffect, useState} from "react";
import {connect} from "react-redux";
import {FormattedMessage, useIntl} from "react-intl";

import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import {downloadImageWithFetch} from "../../../../misc/misc2";
import LookBeforeAfterSlider from "./LookBeforeAfterSlider";
import {loadImagePromise} from "../../../../misc/offlineImagesCache";
import LoadingSpinner from "../../../ui/loadingSpinner";
import LookAIMultiImage from "./LookAIMultiImage";
import LookAILoadingBar from "./LookAILoadingBar";
import AIResultCustomPromptButtons from "./AIResultCustomPromptButtons";
import AIResultCustomPrompt from "./AIResultCustomPrompt";
import {AIDownloadIcon, AiEditIcon} from "../../../common/svgIcons/svgIcons";
import {getPieceGridPosition} from "../../aiCutImageIntoPieces";
import {setLookAIEditPiece} from "../../aiActions";

const EDIT_TRANSITION_MS = 350; // keep in sync with .look-ai__overlay-edit-image / -edit-block transitions

// pieceIndex of lookAIEditPiece when the WHOLE result is being edited (every result that is not
// a multiImage one): there is no 2x2 grid to cut, so there is no piece - the image posted to the
// edit-image endpoint is the result image itself.
const WHOLE_IMAGE_PIECE_INDEX = null;

const isWholeImageEdit = (pieceIndex) => (pieceIndex === null) || (pieceIndex === undefined);

// Geometry of the zoomed piece: it starts as the very quadrant it was cut from (50% x 50%,
// placed in its own corner) and expands from there to the whole image area. The whole-image
// edit has nothing to zoom out of - it covers the whole area from the first frame.
const getEditImageStyle = (pieceIndex, expanded) => {
  if (expanded || isWholeImageEdit(pieceIndex)) {
    return {left: "0%", top: "0%", width: "100%", height: "100%"};
  }
  const {row, column} = getPieceGridPosition(pieceIndex);
  return {
    left: (column * 50) + "%",
    top: (row * 50) + "%",
    width: "50%",
    height: "50%",
  };
}

const LookAIImageOverlayImageContent = (props) => {

  const {aiResults, aiUserFileBlobUrl, opened, closeAIImageOverlay, lookAIEditPiece,
    aiRegenerateLoading, setLookAIEditPiece, isMobile} = props;
  const [imagesLoaded, setImagesLoaded] = useState([]);
  // false right after the piece is picked (it is still sitting in its own corner), true once
  // the expand transition has been kicked off - also drives the image area / edit block, so
  // everything animates at the same time
  const [editExpanded, setEditExpanded] = useState(false);

  const intl = useIntl();

  const activeResult = aiResults.find(aiResult => aiResult.isActive);
  const imageLoaded = activeResult ? imagesLoaded.includes(activeResult.resultImageUrl) : false;
  // a "multiImage" result is one image built out of 4 variants laid out 2x2, see aiApi
  const isMultiImage = !!(activeResult && activeResult.isMultiImage);

  // what is currently opened for a custom prompt edit, in either of its two modes:
  // viewOnly (a piece of a multiImage result was clicked) or the custom prompt mode (its "edit"
  // button was clicked - the prompt block slides in). The before/after slider stays in both
  // modes, so the user can keep comparing while typing the prompt. For every other result the
  // whole image is what gets edited (pieceIndex === WHOLE_IMAGE_PIECE_INDEX), and it is always
  // the custom prompt mode.
  const openedPiece = activeResult && lookAIEditPiece && lookAIEditPiece.dataUrl
    && (lookAIEditPiece.resultId === activeResult.id) ? lookAIEditPiece : null;
  const editing = !!openedPiece;
  const promptEditing = editing && !openedPiece.viewOnly;

  // the typed prompt is deliberately kept - the user gets it back when re-opening the edit mode
  const closeEditMode = () => {
    setEditExpanded(false);
    setLookAIEditPiece(null);
  }

  useEffect(() => {
    const loaded = activeResult ? imagesLoaded.includes(activeResult.resultImageUrl) : false;
    if (!loaded) {
      const loadImages = async () => {
        await loadImagePromise(activeResult.resultImageUrl);
        setImagesLoaded([...imagesLoaded, activeResult.resultImageUrl]);
      }
      loadImages();
    }
  }, [imagesLoaded, activeResult]);

  useEffect(() => {
    if (!editing || editExpanded) {
      return;
    }
    // two frames: the first one mounts the piece over its own quadrant, the second one starts
    // the transition to the full area (a style applied in the mounting frame would not animate)
    const firstFrame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setEditExpanded(true));
    });
    return () => window.cancelAnimationFrame(firstFrame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // switching to another result (or generating a new one) always drops the edit mode
  useEffect(() => {
    if (lookAIEditPiece && (!activeResult || lookAIEditPiece.resultId !== activeResult.id)) {
      closeEditMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeResult, lookAIEditPiece]);

  // Opens what the custom prompt will be applied to. For a multiImage result that is one of the
  // pre-cut pieces (see LookAIMultiImage - the cutting happens up front, so there is nothing to
  // await here): viewOnly=true came from a click on the piece itself, viewOnly=false from its
  // "edit" button; going from the first to the second keeps the piece exactly where it is and
  // only reveals the prompt block underneath. For every other result pieceIndex is
  // WHOLE_IMAGE_PIECE_INDEX and dataUrl is the result image itself.
  const onPieceOpen = (pieceIndex, dataUrl, viewOnly) => {
    if (!activeResult || !dataUrl) {
      return;
    }
    setLookAIEditPiece({
      resultId: activeResult.id,
      pieceIndex,
      dataUrl,
      viewOnly: !!viewOnly,
      regenerateError: "",
    });
  }

  // the "edit" button next to "download" - a non-multiImage result is edited as a whole
  const onWholeImageEditClick = () => {
    onPieceOpen(WHOLE_IMAGE_PIECE_INDEX, activeResult.resultImageUrl, false);
  }

  // the top right close button always closes the whole overlay - an open edit mode is dropped
  // with it, so the overlay never re-opens straight into it
  const onCloseOverlayClick = () => {
    if (editing) {
      closeEditMode();
    }
    closeAIImageOverlay();
  }

  // leaving the edit mode: the close button of the prompt block (AIResultCustomPrompt), or the
  // close button next to "edit" on a piece that is only being looked at (viewOnly)
  const onEditRevertClick = () => {
    setEditExpanded(false); // collapses the piece back into its corner, slides the prompt block out
    window.setTimeout(() => {
      setLookAIEditPiece(null); // the typed prompt stays, only the zoom is dropped
    }, EDIT_TRANSITION_MS + 50);
  }

  if (!activeResult) {
    return null;
  }

  const sourceImageUrl = activeResult.sourceImageUrl || aiUserFileBlobUrl;
  const wholeImageEditing = editing && isWholeImageEdit(openedPiece.pieceIndex);
  // the main slider (over the whole result) is kept while the whole image is being edited - the
  // edited image IS the result image, so there is nothing to zoom and the very same slider (same
  // handle position, no second reveal animation) stays under the prompt block. Only a multiImage
  // piece hides it, that one carries its own slider over the zoomed piece.
  const mainSliderVisible = !isMultiImage && (!editing || wholeImageEditing)
    && !(activeResult.resultImageUrl && !activeResult.sourceImageUrl);
  // a whole-image edit with the slider on does not need the edit image cover at all (it would
  // only hide the slider) - just the regenerate progress bar is shown over the result
  const editImageCoverVisible = editing && !(wholeImageEditing && mainSliderVisible);
  const regenerateLoadingBar = aiRegenerateLoading ? (
    <div className="absolute u-left-0 u-w-full bottom-24 flex flex--center z-10 pointer-events-none">
      <LookAILoadingBar className="look-ai__overlay-edit-loading-bar"/>
    </div>
  ) : null;

  return (
    <div className="flex--1 relative u-w-full u-h-full u-rounded-025 overflow-hidden">
      {/* the image area keeps its natural size while editing - the custom prompt block is
          absolutely positioned and slides up OVER the bottom of the image instead of pushing it */}
      <div className="relative u-w-full look-ai__overlay-image-area">
        <picture>
          {/*<source srcSet={activeResult.resultImageUrl} media="(min-width: 600px)"/>*/}
          <img className={imageLoaded ? "" : "opacity-0 visibility-hidden"}
               src={activeResult.resultImageUrl} alt="Your Generated Design"/>
        </picture>
        {/* the slider stays in the custom prompt edit mode too (whole-image edit). A multiImage
            result has its slider on the opened piece instead (see below). */}
        {imageLoaded ? (!mainSliderVisible
          ? null : <LookBeforeAfterSlider
            opened={opened}
            backgroundImageUrl={sourceImageUrl}
            overlayImageUrl={activeResult.resultImageUrl}
          />) : (
          <div
            className="u-rounded-025 absolute u-left-0 u-top-0 u-w-full u-h-full overflow-hidden flex flex--center">
            <LoadingSpinner/>
          </div>
        )}
        {editImageCoverVisible ? (
          <div className="look-ai__overlay-edit-image"
               style={getEditImageStyle(openedPiece.pieceIndex, editExpanded)}>
            <img src={openedPiece.dataUrl} alt="Your Generated Design"/>
            {/* the comparison with the original photo is kept on the piece both in the "look
                closer" (viewOnly) mode and while a custom prompt is being typed for it - the
                element stays mounted across the switch, so the handle does not jump */}
            {editExpanded && sourceImageUrl ? (
              <LookBeforeAfterSlider opened={editExpanded}
                                     className={"look-ai__overlay-slider--under-pieces"}
                                     backgroundImageUrl={sourceImageUrl}
                                     overlayImageUrl={openedPiece.dataUrl}/>
            ) : null}
            {openedPiece.viewOnly ? (
              <AIResultCustomPromptButtons pieceIndex={openedPiece.pieceIndex}
                                           className="look-ai__overlay-edit-image-buttons"
                                           onCloseClick={onEditRevertClick}
                                           onEditClick={() => onPieceOpen(openedPiece.pieceIndex,
                                             openedPiece.dataUrl, false)}/>
            ) : null}
            {regenerateLoadingBar}
          </div>
        ) : (editing ? regenerateLoadingBar : null)}
        {isMultiImage && imageLoaded && !editing
          ? <LookAIMultiImage resultImageUrl={activeResult.resultImageUrl}
                              onPieceOpen={onPieceOpen}/> : null}
      </div>
      <AIResultCustomPrompt opened={promptEditing && editExpanded}
                            onClose={onEditRevertClick}/>
      {/* a multiImage result carries its edit buttons on the pieces themselves; every other one
          gets "edit" as a plain text button right before "download", styled the same way. Both
          are hidden while the prompt block is open - it takes over the bottom of the overlay. */}
      {isMultiImage ? null : (
        <div className="look-ai__overlay-actions flex u-gap-05 u-right-1 u-bottom-1">
          {isMobile ? (
            <>
              {activeResult.resultImageUrl ? (
                <IconButton aria-label={intl.formatMessage({id: "look.ai.overlay.editImageButton", defaultMessage: "Edit"})}
                            className="look-icon look-ai__overlay-pieces-button"
                            onClick={onWholeImageEditClick}>
                  {AiEditIcon}
                </IconButton>
              ) : null}
              <IconButton aria-label={intl.formatMessage({id: "look.ai.overlay.downloadImageButton"})}
                          className="look-icon look-ai__overlay-pieces-button"
                          onClick={() => downloadImageWithFetch(activeResult.resultImageUrl, "visualizer.png")}>
                {AIDownloadIcon}
              </IconButton>
            </>
          ) : (
            <>
              {activeResult.resultImageUrl ? (
                <Button className="look-ai__overlay-download-button u-pl-2 u-pr-2 nowrap"
                        startIcon={AiEditIcon}
                        onClick={onWholeImageEditClick}>
                  <FormattedMessage id="look.ai.overlay.editImageButton" defaultMessage="Edit"/>
                </Button>
              ) : null}
              <Button className="look-ai__overlay-download-button u-pl-2 u-pr-2 nowrap"
                      startIcon={AIDownloadIcon}
                      onClick={() => downloadImageWithFetch(activeResult.resultImageUrl, "visualizer.png")}>
                <FormattedMessage id="look.ai.overlay.downloadImageButton"/>
              </Button>
            </>
          )}
        </div>
      )}
      <IconButton aria-label={intl.formatMessage({id: "iconButton.close"})}
                  className="look-icon look-icon--small look-icon--white2 rounded-full absolute z-20 u-top-075 u-right-075"
                  onClick={onCloseOverlayClick}>
        <CloseIcon/>
      </IconButton>
    </div>
  )

};

const mapStateToProps = (store) => {
  const {lookOverlayOpened} = store.lookState;
  const {aiUserFileBlobUrl, aiResults, lookAIEditPiece, aiRegenerateLoading} = store.aiState;
  const {isMobile} = store.serverState;

  return {
    isMobile,
    aiResults,
    lookOverlayOpened,
    aiUserFileBlobUrl,
    lookAIEditPiece,
    aiRegenerateLoading,
  };
};

const mapDispatchToProps = {
  setLookAIEditPiece,
};


export default connect(mapStateToProps, mapDispatchToProps)(LookAIImageOverlayImageContent)
